import { useEffect, useRef, useState } from 'react'
import { Search, X } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import { Button, Modal } from '@/components/ui'

interface CustomerResult {
  id: string
  name: string
  source: 'sap' | 'tanss'
  sapBpCode?: string
  tenantId?: string
  tenantCode?: string
}

export interface CustomerSelection {
  customerSapBpCode: string | null
  customerTanssId: string | null
  customerName: string | null
  tenantId?: string | null
}

interface ChangeCustomerModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  description?: string
  confirmLabel?: string
  loading?: boolean
  errorMessage?: string | null
  initialCustomerName?: string | null
  /** Erlaubt es den Kunden zu entfernen (Reset). */
  allowEmpty?: boolean
  /**
   * When set, customer search is scoped to this SAP mandant (use the quote's
   * tenantId here). Prevents CardCode mismatches when the user's globally
   * active mandant differs from the quote's stored mandant.
   */
  tenantId?: string
  onConfirm: (selection: CustomerSelection) => void
}

export function ChangeCustomerModal({
  open,
  onOpenChange,
  title = 'Kunde ändern',
  description,
  confirmLabel = 'Übernehmen',
  loading = false,
  errorMessage = null,
  initialCustomerName = null,
  allowEmpty = false,
  tenantId: _tenantId,
  onConfirm,
}: ChangeCustomerModalProps) {
  const [query, setQuery] = useState('')
  const [suggestions, setSuggestions] = useState<CustomerResult[]>([])
  const [selected, setSelected] = useState<CustomerResult | null>(null)
  const [searchLoading, setSearchLoading] = useState(false)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) {
      setQuery('')
      setSuggestions([])
      setSelected(null)
      setShowSuggestions(false)
      return
    }
    if (initialCustomerName) setQuery(initialCustomerName)
    setTimeout(() => inputRef.current?.focus(), 50)
  }, [open, initialCustomerName])

  useEffect(() => {
    if (!open) return
    if (selected) return
    if (query.trim().length < 2) {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      setSearchLoading(true)
      try {
        const { data } = await apiClient.get('/quotes/customers/search', {
          params: { q: query },
        })
        const list: CustomerResult[] = Array.isArray(data) ? data : []
        setSuggestions(list)
        setShowSuggestions(true)
      } catch {
        setSuggestions([])
        setShowSuggestions(false)
      } finally {
        setSearchLoading(false)
      }
    }, 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query, selected, open])

  function selectPartner(partner: CustomerResult) {
    setSelected(partner)
    setQuery(partner.name)
    setShowSuggestions(false)
    setSuggestions([])
  }

  function clearPartner() {
    setSelected(null)
    setQuery('')
    setSuggestions([])
    setShowSuggestions(false)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  function handleConfirm() {
    if (selected) {
      onConfirm({
        customerSapBpCode: selected.sapBpCode ?? null,
        customerTanssId: selected.source === 'tanss' ? selected.id : null,
        customerName: selected.name,
        tenantId: selected.tenantId ?? null,
      })
      return
    }
    if (allowEmpty) {
      onConfirm({
        customerSapBpCode: null,
        customerTanssId: null,
        customerName: null,
      })
    }
  }

  const canConfirm = !!selected || allowEmpty

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!canConfirm || loading}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {description && (
          <p className="text-small text-gray-600">{description}</p>
        )}

        <div>
          <label className="block text-small font-semibold text-gray-700 mb-1.5">
            Kunde
          </label>
          <div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => {
                  if (selected) clearPartner()
                  setQuery(e.target.value)
                }}
                placeholder="Kundenname oder Kundennummer suchen…"
                className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-md text-body focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 disabled:bg-gray-50"
                autoComplete="off"
                disabled={loading}
              />
              {(query || selected) && !loading && (
                <button
                  type="button"
                  onClick={clearPartner}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  aria-label="Auswahl löschen"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {showSuggestions && (
              <ul className="mt-1 w-full bg-white border border-gray-200 rounded-md shadow-md max-h-48 overflow-auto">
                {searchLoading ? (
                  <li className="px-3 py-2 text-small text-gray-500">Suche…</li>
                ) : suggestions.length === 0 ? (
                  <li className="px-3 py-2 text-small text-gray-500">Keine Treffer</li>
                ) : (
                  suggestions.map((p) => (
                    <li key={`${p.source}-${p.id}`}>
                      <button
                        type="button"
                        onClick={() => selectPartner(p)}
                        className="w-full text-left px-3 py-2 text-small hover:bg-brand-50 focus:outline-none focus:bg-brand-50"
                      >
                        <span className="font-medium text-gray-900">{p.name}</span>
                        {p.sapBpCode && (
                          <span className="ml-2 text-gray-400">{p.sapBpCode}</span>
                        )}
                        {p.tenantCode && (
                          <span className="ml-2 text-caption text-brand-600 font-medium">{p.tenantCode}</span>
                        )}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>
          {selected && (
            <p className="mt-1 text-caption text-brand-700">
              ✓ {selected.name}
              {selected.sapBpCode && ` (${selected.sapBpCode})`}
            </p>
          )}
          {!selected && allowEmpty && (
            <p className="mt-1 text-caption text-gray-500">
              Kein Kunde ausgewählt — der Kunde wird vom Angebot entfernt.
            </p>
          )}
        </div>

        {errorMessage && (
          <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-small text-red-700">
            {errorMessage}
          </div>
        )}
      </div>
    </Modal>
  )
}
